import { useEffect, useRef, useState } from "react";
import { Check, ChevronsUpDown, Plus, Store } from "lucide-react";
import { useTenant } from "@/context/TenantContext";
import { cn } from "@/lib/utils";

export function TenantSwitcher({ collapsed }: { collapsed: boolean }) {
  const { activeSlug, memberships, setActiveBySlug } = useTenant();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const active = memberships.find((m) => m.tenant.slug === activeSlug);
  const label = active?.tenant.display_name ?? "No project";

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (collapsed) {
    // Collapsed sidebar: show just the project initial as an avatar button.
    const initial = (active?.tenant.display_name ?? "?").slice(0, 1).toUpperCase();
    return (
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex h-8 w-8 items-center justify-center rounded-md bg-accent/60 text-xs font-semibold text-foreground hover:bg-accent"
        title={label}
      >
        {initial}
      </button>
    );
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm text-foreground hover:bg-accent/60 transition-colors"
      >
        <Store className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <span className="truncate font-semibold tracking-tight">{label}</span>
        <ChevronsUpDown className="ml-auto h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      </button>
      {open && (
        <div
          className="absolute left-0 right-0 z-20 mt-1 overflow-hidden rounded-md border border-border bg-popover shadow-lg"
          role="listbox"
        >
          <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-muted-foreground">
            Your projects
          </div>
          {memberships.length === 0 ? (
            <div className="px-2 py-2 text-xs text-muted-foreground">
              No tenants — ask an admin to invite you.
            </div>
          ) : (
            memberships.map((m) => (
              <button
                key={m.tenant.slug}
                onClick={() => {
                  void setActiveBySlug(m.tenant.slug);
                  setOpen(false);
                }}
                className={cn(
                  "flex w-full items-center gap-2 px-2 py-1.5 text-left text-sm transition-colors hover:bg-accent/60",
                  m.tenant.slug === activeSlug && "bg-accent/40",
                )}
                role="option"
                aria-selected={m.tenant.slug === activeSlug}
              >
                <span className="flex h-5 w-5 items-center justify-center rounded bg-accent/60 text-[10px] font-semibold">
                  {m.tenant.display_name.slice(0, 1).toUpperCase()}
                </span>
                <span className="min-w-0 flex-1 truncate">{m.tenant.display_name}</span>
                {m.tenant.slug === activeSlug && (
                  <Check className="h-3.5 w-3.5 text-muted-foreground" />
                )}
              </button>
            ))
          )}
          <div className="border-t border-border/60" />
          <button
            disabled
            className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-xs text-muted-foreground/50 cursor-not-allowed"
            title="Coming soon"
          >
            <Plus className="h-3.5 w-3.5" />
            <span>New project</span>
            <span className="ml-auto text-[10px]">Soon</span>
          </button>
        </div>
      )}
    </div>
  );
}
